/**
 * Core Web Vitals Real User Monitoring (RUM)
 * Reports LCP, CLS, INP, FCP, TTFB to analytics and Sentry
 */

import * as Sentry from "@sentry/react";
import {
  observeLCP,
  observeCLS,
  observeINP,
  type PerformanceMetric,
} from "./performance-monitor";

interface VitalReport {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  id: string;
  navigationType: string;
  timestamp: number;
}

/**
 * Send vital metrics to analytics endpoint
 */
async function sendToAnalytics(vital: VitalReport) {
  try {
    // Analytics endpoint removed - vitals are logged locally and sent to Sentry
    // You can add your own analytics endpoint here if needed
    console.debug("[Vitals] Recorded:", vital.name, vital.value);
  } catch (error) {
    console.warn("[Vitals] Failed to process vital:", error);
  }
}

/**
 * Report vital to Sentry as breadcrumb
 */
function reportToSentry(vital: VitalReport) {
  try {
    // Send as breadcrumb for context
    Sentry.addBreadcrumb({
      category: "web-vital",
      message: `${vital.name}: ${vital.value.toFixed(2)}`,
      level: vital.rating === "poor" ? "warning" : "info",
      data: vital,
    });
  } catch (error) {
    console.warn("[Vitals] Failed to report to Sentry:", error);
  }
}

/**
 * Log to console in development
 */
function logVital(vital: VitalReport) {
  if (process.env.NODE_ENV === "development") {
    const emoji =
      vital.rating === "good" ? "✅" : vital.rating === "needs-improvement" ? "⚠️" : "❌";
    console.log(
      `[Vitals] ${emoji} ${vital.name}: ${vital.value.toFixed(2)}ms (${vital.rating})`
    );
  }
}

/**
 * Create a unique ID for this page visit
 */
function generateVisitId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Handle vital metric reporting
 */
function handleVitalReport(metric: PerformanceMetric) {
  const vital: VitalReport = {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    id: generateVisitId(),
    navigationType: (performance.getEntriesByType("navigation")[0] as any)?.type || "navigate",
    timestamp: Date.now(),
  };

  // Log locally
  logVital(vital);

  // Report to Sentry
  reportToSentry(vital);

  // Send to analytics (async, non-blocking)
  sendToAnalytics(vital).catch(console.warn);

  // Store in localStorage for debugging
  storeVitalHistory(vital);
}

/**
 * Store vitals history in localStorage for debugging
 */
function storeVitalHistory(vital: VitalReport) {
  try {
    const key = "hticu-vitals-history";
    const stored = localStorage.getItem(key);
    const history: VitalReport[] = stored ? JSON.parse(stored) : [];
    
    // Keep last 50 vitals
    history.push(vital);
    if (history.length > 50) {
      history.shift();
    }
    
    localStorage.setItem(key, JSON.stringify(history));
  } catch (error) {
    console.warn("[Vitals] Failed to store history:", error);
  }
}

/**
 * Get vitals history from localStorage
 */
export function getVitalsHistory(): VitalReport[] {
  try {
    const stored = localStorage.getItem("hticu-vitals-history");
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Calculate average vitals from history
 */
export function getVitalsStats() {
  const history = getVitalsHistory();
  const grouped = history.reduce((acc, vital) => {
    if (!acc[vital.name]) {
      acc[vital.name] = [];
    }
    acc[vital.name].push(vital.value);
    return acc;
  }, {} as Record<string, number[]>);

  return Object.entries(grouped).map(([name, values]) => ({
    name,
    average: values.reduce((a, b) => a + b, 0) / values.length,
    min: Math.min(...values),
    max: Math.max(...values),
    count: values.length,
  }));
}

/**
 * Initialize Core Web Vitals monitoring
 * Call this once in main.tsx after Sentry initialization
 */
export function initVitalsReporter() {
  console.log("[Vitals] Initializing Core Web Vitals monitoring...");

  // Observe LCP (Largest Contentful Paint)
  observeLCP(handleVitalReport);

  // Observe CLS (Cumulative Layout Shift)
  observeCLS(handleVitalReport);

  // Observe INP (Interaction to Next Paint)
  observeINP(handleVitalReport);

  // Report First Contentful Paint (FCP)
  if ("PerformanceObserver" in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === "first-contentful-paint") {
            handleVitalReport({
              name: "FCP",
              value: entry.startTime,
              rating:
                entry.startTime <= 1800
                  ? "good"
                  : entry.startTime <= 3000
                  ? "needs-improvement"
                  : "poor",
            });
          }
        }
      });

      observer.observe({ type: "paint", buffered: true });
    } catch (error) {
      console.warn("[Vitals] FCP observation failed:", error);
    }
  }

  // Report Time to First Byte (TTFB)
  if ("PerformanceObserver" in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const navigationEntry = entries[0] as PerformanceNavigationTiming;

        if (navigationEntry) {
          const ttfb = navigationEntry.responseStart - navigationEntry.requestStart;
          handleVitalReport({
            name: "TTFB",
            value: ttfb,
            rating: ttfb <= 800 ? "good" : ttfb <= 1800 ? "needs-improvement" : "poor",
          });
        }
      });

      observer.observe({ type: "navigation", buffered: true });
    } catch (error) {
      console.warn("[Vitals] TTFB observation failed:", error);
    }
  }

  console.log("[Vitals] Monitoring active");
}
